import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Spin, Empty, Pagination, message } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { 
  getSessionHistory,
  SessionHistoryItem,
  SessionHistoryResponse,
  getOrFetchContent
} from "@sharpana/auth/api_client/summarization_api";
import { css } from "@emotion/react";
import { playlistsContainer, emptyContainer, darkLoadingContainer } from "./my_playlists_styles";
import * as Sentry from "@sentry/react";

const sessionHistoryContainer = css`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  
  h1 {
    font-size: 24px;
    margin-bottom: 20px;
  }

  .header {
    display: flex;
    align-items: center;
    margin-bottom: 20px;
  }

  .session-list {
    margin-top: 20px;
  }

  .session-item {
    display: flex;
    padding: 4px;
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 16px;
    background-color:rgb(55, 55, 55);
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
    
    .thumbnail {
      width: 180px;
      height: 102px;
      margin-right: 16px;
      overflow: hidden;
      border-radius: 4px;
      flex-shrink: 0;
      background-color: #e0e0e0;
      position: relative;
      
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: opacity 0.3s ease;
        
        &.loading {
          opacity: 0;
        }
        
        &.loaded {
          opacity: 1;
        }
      }
    }
    
    .content {
      flex: 1;
      padding-right: 8px;
      word-wrap: break-word;
      overflow-wrap: break-word;
      
      h3 {
        margin: 0 0 8px 0;
        font-size: 16px;
        font-weight: 500;
        line-height: 1.3;
        color: white;
      }
      
      .url {
        font-size: 14px;
        color: white;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }
      
      .date {
        font-size: 12px;
        color: white;
        margin-top: 4px;
      }
    }
  }
  
  .pagination-container {
    display: flex;
    justify-content: center;
    margin-top: 24px;
  }
`;

export const SessionHistory = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionHistory, setSessionHistory] = useState<SessionHistoryResponse | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(15);

  useEffect(() => {
    fetchSessionHistory();
  }, [currentPage]);

  const fetchSessionHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getSessionHistory(currentPage, pageSize);
      setSessionHistory(response);
    } catch (err) {
      console.error("Failed to fetch session history:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to load session history";
      setError(errorMessage);
      message.error("Failed to load session history");
      Sentry.captureException(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    
    // Scroll to the top of the page
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleSessionClick = async (session: SessionHistoryItem) => {
    try {
      setLoading(true);
      setError(null);
      // Note: We can probably skip this getOrFetchContent() since this item should already exist?
      const result = await getOrFetchContent(session.target_url);
      if (result.error) {
        setError(result.error);
        message.error(result.error);
      } else {
        navigate(`/summary/${result.content_resource_id}?back_enabled=true`);
      }
    } catch (err) {
      console.error("Failed to fetch content:", err);
      const errorMessage = err instanceof Error ? err.message : "Failed to load content";
      setError(errorMessage);
      message.error("Failed to load content");
      Sentry.captureException(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderContent = () => {
    if (loading && !sessionHistory) {
      return (
        <div css={darkLoadingContainer}>
          <Spin size="large" tip="Loading...">
            <div className="loading-content" />
          </Spin>
        </div>
      );
    }

    if (error && !sessionHistory) {
      return (
        <div css={emptyContainer}>
          <Empty 
            description={<span>Error loading session history: {error}</span>}
          />
        </div>
      );
    }

    if (!sessionHistory || !sessionHistory.items || sessionHistory.items.length === 0) {
      return (
        <div css={emptyContainer}>
          <Empty description="No session history found" />
        </div>
      );
    }

    return (
      <>
        <div className="session-list">
          {sessionHistory.items.map((session) => (
            <div 
              key={session.id}
              className="session-item"
              onClick={() => handleSessionClick(session)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSessionClick(session);
                }
              }}
            >
              <div className="thumbnail">
                {session.thumbnail ? (
                  <img
                    src={session.thumbnail}
                    alt={`${session.title} thumbnail`}
                    className="loading"
                    onError={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.src = "/placeholder-thumbnail.jpg";
                    }}
                    onLoad={(e) => {
                      const img = e.target as HTMLImageElement;
                      img.classList.remove("loading");
                      img.classList.add("loaded");
                    }}
                  />
                ) : (
                  <img
                    src="/placeholder-thumbnail.jpg"
                    alt="Default thumbnail"
                    className="loaded"
                  />
                )}
              </div>
              <div className="content">
                <h3>{session.title || "Untitled Resource"}</h3>
                {/* <div className="url">{session.target_url}</div> */}
                {session.created_at && (
                  <div className="date">Viewed on {formatDate(session.created_at)}</div>
                )}
              </div>
            </div>
          ))}
        </div>
        
        {sessionHistory && sessionHistory.total > pageSize && (
          <div className="pagination-container">
            <Pagination
              current={sessionHistory.current_page}
              total={sessionHistory.total}
              pageSize={pageSize}
              onChange={handlePageChange}
              showSizeChanger={false}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <div css={[playlistsContainer, sessionHistoryContainer]}>
      <div className="header">
        <h1>History</h1>
      </div>
      {renderContent()}
    </div>
  );
};